import {
  doc, updateDoc, deleteDoc, getDocs, query, where, collection,
  serverTimestamp, deleteField,
} from "firebase/firestore";
import { db } from "../firebase";
import { buildListingFields } from "../shared/listingRules";
import { encodeGeohash } from "./geohash";
import { REELS_COLLECTION } from "./reelsShared";

/**
 * The `properties` document for setDoc(ref, doc, { merge: true }), built from
 * the listing form values with the same shared rules the mobile app uses,
 * so a listing saved here is indistinguishable from one saved in the app.
 *
 * Create: status 'active', rating/reviews 0 and createdAt; fields that don't
 * apply to the type are simply left out.
 * Edit: fields that no longer apply (e.g. after a type change) are deleted;
 * rating, reviews, status and createdAt are never written: ratings come from
 * verified reviews, and status changes go through hide / delete.
 */
export function buildListingDoc({ id, values, latitude, longitude, stateName, images, owner, isEdit }) {
  const { fields, removedKeys } = buildListingFields(values);
  const lat = Number(latitude);
  const lng = Number(longitude);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);

  const data = {
    id,
    ...fields,
    state: stateName || "",
    latitude: hasCoords ? lat : 0,
    longitude: hasCoords ? lng : 0,
    geohash: hasCoords ? encodeGeohash(lat, lng, 9) : "",
    images: Array.isArray(images) ? images : [],
    ownerId: owner?.ownerId ?? null,
    ownerName: owner?.ownerName || "",
    ownerPhone: owner?.ownerPhone || "",
    ownerAvatar: owner?.ownerAvatar || "",
    ownerRole: owner?.ownerRole || "owner",
    updatedAt: serverTimestamp(),
  };

  if (isEdit) {
    removedKeys.forEach((key) => { data[key] = deleteField(); });
  } else {
    data.status = "active";
    data.rating = 0;
    data.reviews = 0;
    data.createdAt = serverTimestamp();
  }
  return data;
}

/** Hides every Reel of a listing (best-effort), as the app does on delete. */
async function hideReelsForListing(propertyId) {
  try {
    const snap = await getDocs(query(collection(db, REELS_COLLECTION), where("propertyId", "==", propertyId)));
    await Promise.all(snap.docs.map((d) =>
      updateDoc(d.ref, { status: "inactive", updatedAt: serverTimestamp() }).catch(() => {}),
    ));
  } catch {
    // The listing delete still stands; its Reels stay up until the next attempt.
  }
}

/**
 * Tombstones a listing exactly like the app's softDeleteProperty: it leaves
 * every browse surface, but links, saved lists, leads and chat cards resolve
 * to "no longer available" instead of a dead end.
 */
export async function softDeleteListing(propertyId, reason) {
  await updateDoc(doc(db, "properties", propertyId), {
    status: "deleted",
    deletedAt: serverTimestamp(),
    deleteReason: reason,
    deleteReasonText: "",
    updatedAt: serverTimestamp(),
  });
  await hideReelsForListing(propertyId);
}

/** Hidden ('inactive') listings stay with their owner but leave search. */
export function setListingHidden(propertyId, hidden) {
  return updateDoc(doc(db, "properties", propertyId), {
    status: hidden ? "inactive" : "active",
    updatedAt: serverTimestamp(),
  });
}

/** Admins only (the rules allow nobody else to hard-delete). */
export function deleteListingPermanently(propertyId) {
  return deleteDoc(doc(db, "properties", propertyId));
}
